// Right-pane manager PDF preview: packages and individual library pages.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { OpportunityFormatKey } from "@/types/opportunity";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { useAuth } from "@/contexts/AuthContext";
import { renderProposalPdfToBlob } from "@/components/opportunity/pdf/generateProposalPdf";
import { listProposalAlternates } from "@/lib/opportunity/proposalAlternateClient";
import { listProposalPages } from "@/lib/opportunity/proposalPageClient";
import { DEFAULT_PROPOSAL_ALTERNATE_KEY } from "@/lib/opportunity/proposalAlternateTypes";
import { buildManagerProposalPreviewRenderArgs } from "@/lib/opportunity/proposalManagerPreview";
import type { OpportunityDocumentArchetype } from "@/lib/opportunity/documentArchetypes";
import {
  buildPreviewSelectOptions,
  decodePreviewSelection,
  encodePreviewSelection,
  selectionStillValid,
  type PreviewSelectOption,
  type PreviewSelection,
} from "@/lib/opportunity/proposalPreviewSelection";
import { ProposalFormatCollapsibleSection } from "./ProposalFormatCollapsibleSection";

export type ProposalFormatPreviewPanelProps = {
  formatKey: OpportunityFormatKey;
  productType: string;
  /** Bumped on authoring edits — marks preview stale until Refresh (does not auto-render). */
  contentTick?: number;
  archetype?: OpportunityDocumentArchetype;
  /** Controlled selection (package or library page). */
  selection?: PreviewSelection;
  onSelectionChange?: (selection: PreviewSelection) => void;
  /**
   * Bumped by Preview buttons (and when selection is forced) so regenerate
   * runs even if the encoded selection string is unchanged.
   */
  renderRequestId?: number;
};

export function ProposalFormatPreviewPanel(props: ProposalFormatPreviewPanelProps) {
  const {
    formatKey,
    productType,
    contentTick = 0,
    archetype = "proposal",
    selection: controlledSelection,
    onSelectionChange,
    renderRequestId = 0,
  } = props;
  const org = useOrgSettings();
  const auth = useAuth();

  const [internalSelection, setInternalSelection] = useState<PreviewSelection>({
    kind: "package",
    alternateKey: DEFAULT_PROPOSAL_ALTERNATE_KEY,
  });
  const selection = controlledSelection ?? internalSelection;

  const setSelection = useCallback(
    (next: PreviewSelection) => {
      if (onSelectionChange) onSelectionChange(next);
      else setInternalSelection(next);
    },
    [onSelectionChange],
  );

  const [options, setOptions] = useState<PreviewSelectOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [renderedLabel, setRenderedLabel] = useState("Primary");
  const [syncedTick, setSyncedTick] = useState(contentTick);
  const contentTickRef = useRef(contentTick);
  contentTickRef.current = contentTick;
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  const stale = contentTick !== syncedTick;

  const loadOptions = useCallback(async () => {
    const [altRes, pageRes] = await Promise.all([
      listProposalAlternates({
        format_key: formatKey,
        product_type: productType,
        archetype,
      }),
      listProposalPages({
        format_key: formatKey,
        product_type: productType,
        ensure: true,
      }),
    ]);
    const pages =
      pageRes.ok && pageRes.data.pages_supported !== false ? pageRes.data.pages : [];
    const opts = buildPreviewSelectOptions({
      alternates: altRes.ok ? altRes.data.alternates : [],
      pages,
      formatKey,
      productType,
      archetype,
    });
    setOptions(opts);
    if (!selectionStillValid(selectionRef.current, opts)) {
      setSelection({ kind: "package", alternateKey: DEFAULT_PROPOSAL_ALTERNATE_KEY });
    }
  }, [formatKey, productType, archetype, setSelection]);

  useEffect(() => {
    void loadOptions();
  }, [loadOptions, contentTick]);

  const regenerate = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const alternateKey =
        selection.kind === "package"
          ? selection.alternateKey.trim() || DEFAULT_PROPOSAL_ALTERNATE_KEY
          : null;
      const previewPageId = selection.kind === "page" ? selection.pageId : null;
      const args = buildManagerProposalPreviewRenderArgs({
        productType,
        alternateKey,
        previewPageId,
        archetype,
        brandName: org.brandName || org.organizationName,
        organizationName: org.organizationName,
        tagline: org.tagline,
        logoUrl: org.logoUrl,
        proposalContactRow: org.proposalContactRow,
        profile: auth.profile,
      });
      const r = await renderProposalPdfToBlob(args);
      if (!r.ok) {
        setBlob(null);
        setError(r.error);
        return;
      }
      setBlob(r.blob);
      setRenderedLabel(r.alternateLabel);
      setSyncedTick(contentTickRef.current);
    } catch (err) {
      setBlob(null);
      setError(err instanceof Error ? err.message : "Preview failed");
    } finally {
      setBusy(false);
    }
  }, [
    selection,
    renderRequestId,
    productType,
    archetype,
    org.brandName,
    org.organizationName,
    org.tagline,
    org.logoUrl,
    org.proposalContactRow,
    auth.profile,
  ]);

  // Initial render + selection / format / product / explicit Preview requests.
  useEffect(() => {
    void regenerate();
  }, [regenerate]);

  const blobUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const packageOptions = options.filter((o) => o.kind === "package");
  const pageOptions = options.filter((o) => o.kind === "page");

  const description = stale
    ? `Sample pricing · ${renderedLabel} · outdated — refresh to update`
    : `Sample pricing · ${renderedLabel}`;

  const headerRight = (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={encodePreviewSelection(selection)}
        onChange={(e) => {
          const next = decodePreviewSelection(e.target.value);
          if (next) setSelection(next);
        }}
        className="max-w-[14rem] rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
        aria-label="Preview target"
      >
        {packageOptions.length > 0 ? (
          <optgroup label="Packages">
            {packageOptions.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </optgroup>
        ) : null}
        {pageOptions.length > 0 ? (
          <optgroup label="Pages">
            {pageOptions.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
      <button
        type="button"
        disabled={busy}
        onClick={() => void regenerate()}
        aria-label={stale ? "Refresh PDF — preview is outdated" : "Refresh PDF"}
        className={
          stale
            ? "rounded-lg border border-amber-500 bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-950 ring-2 ring-amber-400/80 hover:bg-amber-200 disabled:opacity-50 dark:border-amber-500 dark:bg-amber-950/50 dark:text-amber-100 dark:ring-amber-500/60 dark:hover:bg-amber-900/60"
            : "rounded-lg border border-indigo-300 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
        }
      >
        {busy ? "Rendering…" : stale ? "Refresh PDF · outdated" : "Refresh PDF"}
      </button>
    </div>
  );

  return (
    <ProposalFormatCollapsibleSection
      title="Document preview"
      description={description}
      defaultOpen
      headerRight={headerRight}
    >
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {error}
        </div>
      ) : null}

      <div className="min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-950">
        {blobUrl ? (
          <iframe
            title="Proposal document preview"
            src={blobUrl}
            className="h-[min(70vh,40rem)] w-full"
          />
        ) : (
          <div className="flex h-[min(70vh,40rem)] items-center justify-center text-xs text-gray-500 dark:text-zinc-400">
            {busy ? "Rendering preview…" : "No preview yet"}
          </div>
        )}
      </div>
    </ProposalFormatCollapsibleSection>
  );
}
